import { assertUniqueBy } from '@upright/common/utils/assertion-helpers';
import { Dictionary, get, groupBy, has, isNull, keyBy } from 'lodash';

// IDs are widened to `number | string` so the same helper can be reused for
// FE-side hierarchical models that key off string ids (e.g. `PiuProductState`,
// which mixes stringified BlanketProduct ids with client-generated UUIDs).
// Lodash's `keyBy` / `groupBy` already stringify keys internally, so the
// runtime behavior is unchanged for callers using numeric ids. See PLT-4748.
//
// `parentId` is intentionally an optional property (not required-with-
// undefined): some consumer types declare it optional, and TypeScript treats
// `parentId: X | undefined` and `parentId?: X` as non-equivalent during
// constraint checking.
type HierarchicalId = number | string;
type Hierarchical = {
  id: HierarchicalId;
  parentId?: HierarchicalId | null;
};

export type WithChildren<T> = T & {
  children: Array<WithChildren<T>>;
};

export function groupHierarchically<Tdata extends Hierarchical>(collection: Tdata[]): Array<WithChildren<Tdata>> {
  assertUniqueBy(collection, ['id']);
  const productsById = keyBy(collection, product => product.id);
  const childrenByParentId = groupBy(collection, product => product.parentId);

  const topLevelProducts = collection.filter(product => !(product.parentId && has(productsById, product.parentId)));
  const hierarchical = topLevelProducts.map(topLevelProduct =>
    groupHierarchicallyRecursive(topLevelProduct.id, productsById, childrenByParentId),
  );
  return hierarchical;
}

function groupHierarchicallyRecursive<T extends { id: HierarchicalId }>(
  elementId: HierarchicalId,
  elementsById: Dictionary<T>,
  elementsByParentId: Dictionary<T[]>,
): WithChildren<T> {
  const element = get(elementsById, elementId, null);
  if (isNull(element)) {
    throw Error(`Element ${elementId} unexpectedly not found`);
  }
  const childElements: T[] = get(elementsByParentId, elementId, []);
  const subRows = childElements.map(childElement =>
    groupHierarchicallyRecursive(childElement.id, elementsById, elementsByParentId),
  );
  return {
    ...element,
    children: subRows,
  };
}

/**
 * Hard cap on nesting depth for PLG segment / product hierarchies (PLT-5689).
 * A top-level node is depth 1 and `MAX_SEGMENT_DEPTH` counts levels inclusive of
 * the leaf, so a value of 5 allows a root plus four levels of descendants.
 * Matches the Blanketti blanket-product-set editor's cap (its `maxDepth = 4` is
 * 0-indexed, i.e. 5 levels) for backward compatibility (PLT-5690). Shared so the
 * backend upsert guard (`assertHierarchyValid`) and the frontend DnD / add-child
 * feedback enforce the same limit.
 */
export const MAX_SEGMENT_DEPTH = 5;

/**
 * Validate a flat parent/child collection before it is persisted or grouped.
 * Throws on:
 *  - a `parentId` cycle (which `groupHierarchically` would otherwise silently
 *    drop — cyclic nodes are unreachable from any root — losing products); and
 *  - a chain deeper than `maxDepth`.
 * A `null` / missing / dangling `parentId` (parent not in the set) ends the
 * chain as a top-level node, consistent with `groupHierarchically`. Returns
 * nothing on success.
 */
export function assertHierarchyValid<T extends Hierarchical>(
  collection: T[],
  maxDepth: number = MAX_SEGMENT_DEPTH,
): void {
  // Stringify keys so numeric and string/uuid ids compare consistently — the
  // same normalisation lodash's `keyBy`/`groupBy` apply inside
  // `groupHierarchically`, so a dangling parentId is judged identically here.
  const byId = new Map<string, T>(collection.map(item => [String(item.id), item]));
  for (const start of collection) {
    const seen = new Set<string>();
    let current: T | undefined = start;
    let depth = 0;
    while (current) {
      const key = String(current.id);
      if (seen.has(key)) {
        throw new Error(`Product hierarchy contains a cycle involving id ${current.id}`);
      }
      seen.add(key);
      depth += 1;
      if (depth > maxDepth) {
        throw new Error(`Product hierarchy exceeds the maximum nesting depth of ${maxDepth}`);
      }
      // A null / missing / dangling parentId ends the chain as a top-level node.
      current = current.parentId != null ? byId.get(String(current.parentId)) : undefined;
    }
  }
}

export type WithSubRows<Tdata> = Tdata & {
  subRows: Array<WithSubRows<Tdata>>;
};

/** Used to generate "subRows" from "children" because that is TableV2's prop for hierarchy  */
export function formatToSubRows<T extends Hierarchical>(element: WithChildren<T>): WithSubRows<WithChildren<T>> {
  return { ...element, subRows: element.children.map(formatToSubRows) };
}
